import { useEffect } from 'react';
import { Flex, Icon, NakedButton } from '@qga/roo-ui/components';

import Skeleton from '@shared/v2/components/Skeleton';
import useOfferUpgrade from '@shared/v2/hooks/useOfferUpgrade';
import usePackageSearch from '@shared/v2/hooks/usePackageSearch';
import useToggle from '@shared/v2/hooks/useToggle';
import { PackageOption as PackageOptionType } from '@shared/v2/utils/getPackageOptions';

import useProcuredOffer from '@/components/PackagePage/hooks/useProcuredOffer';
// Components
import Text from '@/components/Text';
import useBreakpoints, { isScreen } from '@/hooks/useBreakpoints';
// Hooks
import usePropertyContent from '@/hooks/usePropertyContent';
import { format } from '@/lib/date';
import { useFlightModal } from '@/v2/components/pages/PackageViewV2/contexts/FlightModalContext';
// Context hooks
import { useRoomOptionsData } from '@/v2/components/pages/PackageViewV2/contexts/RoomOptionsContext';
import { useSearchPanelControls } from '@/v2/components/pages/PackageViewV2/contexts/SearchPanelContext';
import { useGA4Events } from '@/v2/hooks/featureFlags/ga4Events/useGA4Events';
// Types
import { UI } from '@/v2/types';
import { dataLayer } from '@/v2/utils/dataLayer';
import MainCTA from '../MainCTA';
import OfferModal from '../OfferModal';
import OfferPrice from '../OfferPrice';
import OtherOption from '../OtherOption';
import MobilePackageDetails from './components/MobilePackageDetails';
import MobileSearchPanelModal from './components/MobileSearchPanelModal';

const MobileSearchPanel = ({
  packageOptions,
}: {
  packageOptions: PackageOptionType[];
}) => {
  const {
    openCalendar,
    openMobileSearchPanelModal,
    isMobileSearchPanelModalOpen,
    closeMobileSearchPanelModal,
  } = useSearchPanelControls();
  const shouldUseNewEvents = useGA4Events();

  const breakpoints = useBreakpoints();
  const smallScreen = isScreen(breakpoints)('xs', 'sm');

  const {
    value: isUpgradeModalOpen,
    setOn: openUpgradeModal,
    setOff: closeUpgradeModal,
  } = useToggle(false);

  const {
    value: isPackageDetailsOpen,
    setOn: openPackageDetails,
    setOff: closePackageDetails,
  } = useToggle(false);

  const propertyContent: UI.Property = usePropertyContent();
  const procuredOffer: UI.ProcuredOffer | undefined = useProcuredOffer();
  const { selectedRoomOption } = useRoomOptionsData() || {};
  const upgradeOffer = useOfferUpgrade({
    inclusionSets: procuredOffer?.inclusionSets,
    selectedRoomOption,
  });

  const {
    data: { departureDate, returnDate },
  } = usePackageSearch();

  const { openFlightModal } = useFlightModal({
    roomId: selectedRoomOption?.roomType.id,
  });

  const handleOpenCalendar = () => {
    openMobileSearchPanelModal();
    openCalendar();
  };

  const handleReserve = () => {
    const isPoOffer = !!selectedRoomOption?.matchingOffer?.leadInOffer;
    if (isPoOffer && upgradeOffer) {
      openUpgradeModal();
    } else {
      openFlightModal({
        offerId: selectedRoomOption?.matchingOffer?.hotelOffer.id,
      });
    }
  };

  const handleUpgrade = async () => {
    if (shouldUseNewEvents) {
      dataLayer.ctaClickEvent({
        itemText: 'Upgrade package',
      });
    }
    await openFlightModal({
      offerId: upgradeOffer?.hotelOffer.id,
    });
    closeUpgradeModal();
  };

  const handleOpenFlightModal = async () => {
    await openFlightModal({
      offerId: selectedRoomOption?.matchingOffer?.hotelOffer.id,
    });
    closeUpgradeModal();
  };

  // Close modal whenever leaving mobile viewport
  useEffect(() => {
    !smallScreen && closeMobileSearchPanelModal();
  }, [closeMobileSearchPanelModal, smallScreen]);

  return (
    <Flex justifyContent="flex-end">
      {selectedRoomOption?.matchingOffer && (
        <Flex flexDirection="column" mr="4" alignItems="flex-end">
          <OfferPrice offer={selectedRoomOption.matchingOffer} />
          {returnDate && departureDate && (
            <NakedButton onClick={handleOpenCalendar} my="1">
              <Text fontSize="xs" textDecoration="underline" color="text.body">
                {format('d MMM')(departureDate)} - {format('d MMM')(returnDate)}
              </Text>
              <Icon name="edit" size={15} ml="1" color="greys.steel" />
            </NakedButton>
          )}
          <NakedButton onClick={openPackageDetails}>
            <Text bold link fontSize={['xs', null, 'sm']}>
              View package details
            </Text>
          </NakedButton>
        </Flex>
      )}
      <Flex
        flexDirection="column"
        width={selectedRoomOption?.matchingOffer ? '170px' : '100%'}
      >
        <Skeleton height="48px" width="100%">
          <MainCTA isMobileVersion handleReserve={handleReserve} />
        </Skeleton>
        <OtherOption
          upgradeOffer={upgradeOffer}
          selectedRoomOption={selectedRoomOption}
          propertyContent={propertyContent}
          procuredOffer={procuredOffer}
        />
      </Flex>
      <OfferModal.Upgrade
        isOpen={isUpgradeModalOpen}
        onCancel={handleOpenFlightModal}
        onSubmit={handleUpgrade}
        onClose={closeUpgradeModal}
        propertyContent={propertyContent}
        procuredOffer={procuredOffer}
        offer={upgradeOffer}
        roomType={selectedRoomOption?.roomType}
      />
      {isMobileSearchPanelModalOpen && (
        <MobileSearchPanelModal onClose={closeMobileSearchPanelModal} />
      )}
      {isPackageDetailsOpen && (
        <MobilePackageDetails
          packageOptions={packageOptions}
          selectedRoomOption={selectedRoomOption}
          onClose={closePackageDetails}
        />
      )}
    </Flex>
  );
};

export default MobileSearchPanel;
